import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn, PillTag, Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type CoachNotificationAudience,
  type CoachNotificationCategory,
  type CoachNotification,
  type Tier,
} from "@/lib/store";

/**
 * CoachNotifications
 *
 * Coach Kiley's notification composer. She picks an audience, a category,
 * writes a title and body, sees a live device-style preview, and either
 * sends right now or schedules for later. Past sends are listed below as
 * an audit trail with recipient counts.
 *
 * Per Privacy Policy Section 9, body text should not include sensitive
 * health details. We surface a soft inline warning if a draft contains
 * obvious sensitive keywords; she can still send (this is guidance, not
 * a hard block).
 */

interface CategoryDef {
  key: CoachNotificationCategory;
  label: string;
  helper: string;
  icon: string;
}

const CATEGORIES: CategoryDef[] = [
  {
    key: "scripture",
    label: "Scripture",
    helper: "A verse or short reflection.",
    icon: "menu_book",
  },
  {
    key: "encouragement",
    label: "Encouragement",
    helper: "A note of grace for the week.",
    icon: "favorite",
  },
  {
    key: "class-reminder",
    label: "Class reminder",
    helper: "Live class or gathering nudge.",
    icon: "groups",
  },
  {
    key: "announcement",
    label: "Announcement",
    helper: "Founding offers, new features, news.",
    icon: "campaign",
  },
];

interface AudienceDef {
  key: CoachNotificationAudience;
  label: string;
  helper: string;
}

const AUDIENCES: AudienceDef[] = [
  {
    key: "all",
    label: "All sisters",
    helper: "Everyone with notifications enabled.",
  },
  {
    key: "tier",
    label: "By tier",
    helper: "Pick Seedling, Rooted, or Flourishing.",
  },
  {
    key: "founding-members",
    label: "Founding members",
    helper: "The half-off-for-life crew.",
  },
  {
    key: "needs-love",
    label: "Sisters who need love",
    helper: "Resting or zero-streak this week.",
  },
];

/* Light-touch sensitive-keyword check, mirroring the Privacy Policy promise
 * to keep notification text general. Not an exhaustive filter, just a nudge. */
const SENSITIVE_KEYWORDS = [
  "pregnancy",
  "pregnant",
  "miscarriage",
  "miscarried",
  "weight",
  "lbs",
  "pounds",
  "cycle",
  "period",
  "ED",
  "diagnosis",
  "disorder",
];

function detectSensitive(text: string): string | null {
  const lower = text.toLowerCase();
  const hit = SENSITIVE_KEYWORDS.find((k) => lower.includes(k.toLowerCase()));
  return hit ?? null;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function audienceLabel(n: CoachNotification): string {
  if (n.audience === "all") return "All sisters";
  if (n.audience === "tier") return `${n.audienceTier ?? "All"} tier`;
  if (n.audience === "founding-members") return "Founding members";
  if (n.audience === "needs-love") return "Needs love";
  if (n.audience === "single") return "One sister";
  return n.audience;
}

export default function CoachNotifications() {
  const [, navigate] = useLocation();
  const me = useStoreQuery(queries.currentUser);
  const authorId = "u_kiley";
  const history = useStoreQuery(() => queries.coachNotificationsByAuthor(authorId));
  const send = useStoreMutation(mutations.sendCoachNotification);

  const [audience, setAudience] = useState<CoachNotificationAudience>("all");
  const [audienceTier, setAudienceTier] = useState<Tier>("Rooted");
  const [category, setCategory] = useState<CoachNotificationCategory>("encouragement");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [justSentId, setJustSentId] = useState<string | null>(null);

  const recipientCount = useStoreQuery(() =>
    queries.audienceCount({ audience, audienceTier }),
  );

  const titleCharsLeft = 65 - title.length;
  const bodyCharsLeft = 178 - body.length;
  const overTitle = titleCharsLeft < 0;
  const overBody = bodyCharsLeft < 0;

  const sensitiveTitle = useMemo(() => detectSensitive(title), [title]);
  const sensitiveBody = useMemo(() => detectSensitive(body), [body]);
  const sensitiveHit = sensitiveTitle ?? sensitiveBody;

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !overTitle &&
    !overBody &&
    recipientCount > 0;

  function handleSend() {
    if (!canSend) return;
    const row = send({
      authorId,
      audience,
      audienceTier: audience === "tier" ? audienceTier : undefined,
      title,
      body,
      category,
    });
    setJustSentId(row.id);
    setTitle("");
    setBody("");
    setTimeout(() => setJustSentId(null), 4000);
  }

  return (
    <div className="pb-24" data-testid="page-coach-notifications">
      <ScreenHeader
        variant="forest"
        eyebrow="Coach Kiley"
        title="Send a note"
        subtitle="A scripture, a nudge, a word of grace, sent to the sisters who need it."
      />

      {/* Audience picker */}
      <div className="px-5 mt-2">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 8 }}>
          Who hears this
        </p>
        <div className="grid grid-cols-2 gap-2">
          {AUDIENCES.map((a) => {
            const active = a.key === audience;
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => setAudience(a.key)}
                data-testid={`audience-${a.key}`}
                style={{
                  textAlign: "left",
                  padding: 12,
                  borderRadius: 14,
                  border: `1px solid ${active ? "rgba(30,74,53,0.85)" : "rgba(45,62,48,0.15)"}`,
                  background: active
                    ? "rgba(30,74,53,0.08)"
                    : "rgba(245,240,232,0.55)",
                  cursor: "pointer",
                  transition: "all 150ms ease",
                }}
              >
                <p
                  className="mnf-serif"
                  style={{
                    color: active ? "var(--mnf-f)" : "var(--mnf-body)",
                    fontSize: 13.5,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {a.label}
                </p>
                <p
                  className="mnf-body mt-0.5"
                  style={{
                    color: "var(--mnf-body)",
                    fontSize: 11.5,
                    lineHeight: 1.35,
                    opacity: 0.85,
                  }}
                >
                  {a.helper}
                </p>
              </button>
            );
          })}
        </div>

        {audience === "tier" && (
          <div className="mt-2.5 flex gap-2">
            {(["Seedling", "Rooted", "Flourishing"] as Tier[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setAudienceTier(t)}
                data-testid={`tier-${t.toLowerCase()}`}
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  border: `1px solid ${
                    audienceTier === t ? "rgba(30,74,53,0.85)" : "rgba(45,62,48,0.18)"
                  }`,
                  background:
                    audienceTier === t ? "rgba(30,74,53,0.08)" : "transparent",
                  color: "var(--mnf-f)",
                  cursor: "pointer",
                  fontSize: 13,
                }}
                className="mnf-serif"
              >
                {t}
              </button>
            ))}
          </div>
        )}

        <p
          className="mnf-body mt-2.5"
          style={{ color: "var(--mnf-body)", fontSize: 12.5, opacity: 0.85 }}
          data-testid="text-recipient-count"
        >
          <Icon name="group" size={14} color="rgba(45,62,48,0.6)" />{" "}
          This will reach <strong>{recipientCount}</strong>{" "}
          {recipientCount === 1 ? "sister" : "sisters"}.
        </p>
      </div>

      {/* Category picker */}
      <div className="px-5 mt-5">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 8 }}>
          Category
        </p>
        <div className="grid grid-cols-2 gap-2">
          {CATEGORIES.map((c) => {
            const active = c.key === category;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                data-testid={`category-${c.key}`}
                style={{
                  textAlign: "left",
                  padding: 12,
                  borderRadius: 14,
                  border: `1px solid ${
                    active ? "rgba(192,120,74,0.85)" : "rgba(45,62,48,0.15)"
                  }`,
                  background: active
                    ? "rgba(192,120,74,0.08)"
                    : "rgba(245,240,232,0.55)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <Icon
                  name={c.icon}
                  size={18}
                  color={active ? "#C0784A" : "rgba(45,62,48,0.55)"}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-serif"
                    style={{
                      color: "var(--mnf-f)",
                      fontSize: 13.5,
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {c.label}
                  </p>
                  <p
                    className="mnf-body mt-0.5"
                    style={{
                      color: "var(--mnf-body)",
                      fontSize: 11.5,
                      lineHeight: 1.35,
                      opacity: 0.85,
                    }}
                  >
                    {c.helper}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Composer */}
      <div className="px-5 mt-5">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 8 }}>
          Write the note
        </p>
        <GlassCard organic padding={14}>
          <div className="space-y-3">
            <div>
              <label
                className="mnf-body block mb-1.5"
                style={{
                  color: "var(--mnf-body)",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="A gentle subject line"
                data-testid="input-title"
                className="mnf-serif w-full"
                style={{
                  fontSize: 14,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${overTitle ? "#C0784A" : "rgba(45,62,48,0.18)"}`,
                  background: "rgba(245,240,232,0.6)",
                  color: "var(--mnf-f)",
                }}
              />
              <p
                className="mnf-body mt-1"
                style={{
                  fontSize: 11,
                  color: overTitle ? "#C0784A" : "rgba(45,62,48,0.55)",
                  textAlign: "right",
                }}
              >
                {titleCharsLeft} characters left
              </p>
            </div>
            <div>
              <label
                className="mnf-body block mb-1.5"
                style={{
                  color: "var(--mnf-body)",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Body
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Keep it warm and general. Sensitive details stay inside the app."
                rows={4}
                data-testid="input-body"
                className="mnf-body w-full"
                style={{
                  fontSize: 13.5,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${overBody ? "#C0784A" : "rgba(45,62,48,0.18)"}`,
                  background: "rgba(245,240,232,0.6)",
                  color: "var(--mnf-f)",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
              <p
                className="mnf-body mt-1"
                style={{
                  fontSize: 11,
                  color: overBody ? "#C0784A" : "rgba(45,62,48,0.55)",
                  textAlign: "right",
                }}
              >
                {bodyCharsLeft} characters left
              </p>
            </div>

            {sensitiveHit && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 10,
                  background: "rgba(160,120,32,0.10)",
                  border: "1px solid rgba(160,120,32,0.25)",
                }}
                data-testid="warning-sensitive"
              >
                <div className="flex items-start gap-2">
                  <Icon name="warning" size={16} color="#A07820" />
                  <p
                    className="mnf-body"
                    style={{
                      color: "#A07820",
                      fontSize: 12,
                      lineHeight: 1.45,
                    }}
                  >
                    Heads up, the word "<strong>{sensitiveHit}</strong>" looks
                    sensitive. Per our Privacy Policy, push text stays general.
                    You can still send, but consider keeping the specifics in
                    the app and using a softer outside line.
                  </p>
                </div>
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* Live device preview */}
      <div className="px-5 mt-5">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 8 }}>
          How it will look
        </p>
        <div
          style={{
            background: "linear-gradient(135deg, #1E4A35 0%, #163828 100%)",
            borderRadius: 22,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "rgba(245,240,232,0.96)",
              borderRadius: 14,
              padding: "10px 12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
            data-testid="preview-notification"
          >
            <div className="flex items-start gap-2.5">
              <div
                className="shrink-0 flex items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "#1E4A35",
                  color: "#F5F0E8",
                }}
              >
                <Icon name="local_florist" size={16} color="#F5F0E8" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p
                    className="mnf-serif"
                    style={{
                      color: "#1E4A35",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.2,
                    }}
                  >
                    MY NOURISHED FAITH
                  </p>
                  <span
                    style={{
                      color: "rgba(45,62,48,0.5)",
                      fontSize: 11,
                    }}
                  >
                    now
                  </span>
                </div>
                <p
                  className="mnf-serif mt-1"
                  style={{
                    color: "var(--mnf-f)",
                    fontSize: 13.5,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {title || "Your title appears here"}
                </p>
                <p
                  className="mnf-body mt-0.5"
                  style={{
                    color: "var(--mnf-body)",
                    fontSize: 12.5,
                    lineHeight: 1.4,
                  }}
                >
                  {body || "And a soft, warm body of two short sentences max."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send */}
      <div className="px-5 mt-4">
        <Btn
          variant="primary"
          block
          disabled={!canSend}
          onClick={handleSend}
          data-testid="button-send"
        >
          Send to {recipientCount} {recipientCount === 1 ? "sister" : "sisters"}
        </Btn>
        {justSentId && (
          <p
            className="mnf-body mt-2 text-center"
            style={{ color: "#1E4A35", fontSize: 12.5 }}
            data-testid="text-send-confirm"
          >
            <Icon name="check_circle" size={14} color="#1E4A35" /> Sent. The
            sisters will hear from you within a moment.
          </p>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="px-5 mt-7">
          <p
            className="mnf-eyebrow"
            style={{ color: "var(--mnf-f)", marginBottom: 8 }}
          >
            Recently sent
          </p>
          <div className="space-y-2">
            {history.slice(0, 8).map((n) => (
              <GlassCard
                key={n.id}
                organic
                padding={12}
                data-testid={`row-history-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  <Avatar initials="KM" size={36} ringColor="rgba(192,120,74,0.55)" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <PillTag tone="forest">{audienceLabel(n)}</PillTag>
                      <PillTag tone="neutral">{n.category}</PillTag>
                      <span
                        className="mnf-body"
                        style={{
                          color: "rgba(45,62,48,0.55)",
                          fontSize: 11.5,
                          marginLeft: "auto",
                        }}
                      >
                        {n.sentAt ? relativeTime(n.sentAt) : "queued"}
                      </span>
                    </div>
                    <p
                      className="mnf-serif"
                      style={{
                        color: "var(--mnf-f)",
                        fontSize: 13.5,
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {n.title}
                    </p>
                    <p
                      className="mnf-body mt-0.5"
                      style={{
                        color: "var(--mnf-body)",
                        fontSize: 12.5,
                        lineHeight: 1.4,
                      }}
                    >
                      {n.body}
                    </p>
                    <p
                      className="mnf-body mt-1.5"
                      style={{
                        color: "rgba(45,62,48,0.55)",
                        fontSize: 11.5,
                      }}
                    >
                      Reached {n.recipientCount}{" "}
                      {n.recipientCount === 1 ? "sister" : "sisters"}
                    </p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
